import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText, DollarSign, Receipt, Percent, ShieldCheck, Clock, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n || 0);

export default function AccountantPortal() {
  const { token } = useParams();

  const { data, isLoading, error } = useQuery({
    queryKey: ['accountant-portal', token],
    queryFn: async () => { const { data } = await axios.get(`${API}/api/accountant/portal/${token}`); return data; },
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl border border-red-100 p-10 max-w-md text-center">
          <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 mb-2">Invalid or Expired Link</h2>
          <p className="text-slate-500 text-sm">This accountant portal link is no longer valid. Please request a new link from your client.</p>
        </div>
      </div>
    );
  }

  const { user_name, user_email, expires_at, summary, receipts = [], invoices = [], documents = [] } = data;
  const expiresDate = expires_at ? format(new Date(expires_at), 'dd MMM yyyy') : '';

  const statCards = [
    { label: 'Total Expenses', value: fmt(summary.total_expenses), icon: DollarSign, bgColor: 'bg-red-100', iconColor: 'text-red-600' },
    { label: 'Total Income', value: fmt(summary.total_income), icon: DollarSign, bgColor: 'bg-emerald-100', iconColor: 'text-emerald-600' },
    { label: 'GST Collected', value: fmt(summary.total_gst), icon: Percent, bgColor: 'bg-blue-100', iconColor: 'text-blue-600' },
    { label: 'Tax Deductible', value: fmt(summary.tax_deductible), icon: FileText, bgColor: 'bg-purple-100', iconColor: 'text-purple-600' },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="accountant-portal">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">ExpensIQ Accountant Portal</h1>
                <p className="text-sm text-slate-500">Read-only access for <span className="font-medium text-slate-700">{user_name}</span> ({user_email})</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <Clock className="w-4 h-4" />
              Expires {expiresDate}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {statCards.map((stat, i) => (
            <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid={`accountant-stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className={`w-11 h-11 rounded-xl ${stat.bgColor} flex items-center justify-center mb-3`}>
                <stat.icon className={`w-5 h-5 ${stat.iconColor}`} />
              </div>
              <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
              <p className="text-sm text-slate-500 mt-0.5">{stat.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Receipts Table */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm mb-6 overflow-hidden">
          <div className="p-5 border-b border-slate-100 flex items-center gap-2">
            <Receipt className="w-5 h-5 text-slate-600" />
            <h3 className="font-semibold text-slate-900">Receipts ({receipts.length})</h3>
          </div>
          {receipts.length === 0 ? (
            <div className="p-8 text-center text-slate-400 text-sm">No receipts</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-xs text-slate-500 uppercase tracking-wider border-b border-slate-100">
                    <th className="px-5 py-3">Date</th>
                    <th className="px-5 py-3">Merchant</th>
                    <th className="px-5 py-3">Category</th>
                    <th className="px-5 py-3">Amount</th>
                    <th className="px-5 py-3">GST</th>
                    <th className="px-5 py-3">Tax Deductible</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {receipts.slice(0, 50).map((r, i) => (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="px-5 py-3 text-sm text-slate-600">{r.date}</td>
                      <td className="px-5 py-3 text-sm font-medium text-slate-900">{r.store_name || r.merchant || '-'}</td>
                      <td className="px-5 py-3"><span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-600 capitalize">{r.category}</span></td>
                      <td className="px-5 py-3 text-sm font-medium text-slate-900">{fmt(r.amount)}</td>
                      <td className="px-5 py-3 text-sm text-slate-600">{r.gst_type || 'none'}</td>
                      <td className="px-5 py-3 text-sm">{r.tax_deductible ? <span className="text-emerald-600 font-medium">Yes</span> : <span className="text-slate-400">No</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Invoices Table */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm mb-6 overflow-hidden">
          <div className="p-5 border-b border-slate-100 flex items-center gap-2">
            <FileText className="w-5 h-5 text-slate-600" />
            <h3 className="font-semibold text-slate-900">Invoices ({invoices.length})</h3>
          </div>
          {invoices.length === 0 ? (
            <div className="p-8 text-center text-slate-400 text-sm">No invoices</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-xs text-slate-500 uppercase tracking-wider border-b border-slate-100">
                    <th className="px-5 py-3">Invoice #</th>
                    <th className="px-5 py-3">Client</th>
                    <th className="px-5 py-3">Date</th>
                    <th className="px-5 py-3">Amount</th>
                    <th className="px-5 py-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {invoices.slice(0, 50).map((inv, i) => (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="px-5 py-3 text-sm font-medium text-slate-900">{inv.invoice_number || '-'}</td>
                      <td className="px-5 py-3 text-sm text-slate-600">{inv.client_name || '-'}</td>
                      <td className="px-5 py-3 text-sm text-slate-600">{inv.date || '-'}</td>
                      <td className="px-5 py-3 text-sm font-medium text-slate-900">{fmt(inv.total || inv.amount)}</td>
                      <td className="px-5 py-3"><span className={`text-xs px-2 py-1 rounded-full font-medium ${inv.status === 'paid' ? 'bg-emerald-50 text-emerald-600' : inv.status === 'sent' ? 'bg-blue-50 text-blue-600' : 'bg-slate-100 text-slate-500'}`}>{inv.status || 'draft'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Documents */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-slate-100 flex items-center gap-2">
            <FileText className="w-5 h-5 text-slate-600" />
            <h3 className="font-semibold text-slate-900">Documents ({documents.length})</h3>
          </div>
          {documents.length === 0 ? (
            <div className="p-8 text-center text-slate-400 text-sm">No documents</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {documents.slice(0, 50).map((doc, i) => (
                <div key={i} className="px-5 py-3 flex items-center justify-between hover:bg-slate-50">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{doc.name}</p>
                    <p className="text-xs text-slate-400">{doc.type} {doc.expiry_date ? `• Expires ${doc.expiry_date}` : ''}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
